import { Card, CardContent } from "@material-ui/core";
import {
  FormProvider,
  SubmitHandler,
  useForm,
  useFormContext,
} from "react-hook-form";
import { CompactFormToolbar } from "../common/form/CompactFormToolbar";
import { CompactSaveButton } from "../common/form/CompactSaveButton";
import classnames from "classnames";
import { HookFormPasswordInput } from "../common/form/hook-form/inputs/HookFormPasswordInput";
import { Loading, useGetIdentity, useNotify, useTranslate } from "react-admin";
import { handleAxiosError } from "../../common/data/axiosErrorHandler";
import { useSelector } from "react-redux";
import { AppState } from "../../common/types";
import { useCallback, useEffect, useState } from "react";

interface ChangeFleetManagerPasswordDto {
  /**
   *
   * @type {string}
   * @memberof ChangeFleetManagerPasswordDto
   */
  oldPassword: string;
  /**
   *
   * @type {string}
   * @memberof ChangeFleetManagerPasswordDto
   */
  newPassword: string;
  /**
   *
   * @type {string}
   * @memberof ChangeFleetManagerPasswordDto
   */
  repeatNewPassword: string;
}

interface RequestPasswordChangeLinkResultDto {
  isMasterAdmin: boolean;
  isSent: boolean;
}

interface Props {
  onFinished: () => void;
}

const DefaultCompactFormToolbar = () => {
  const {
    formState: { isSubmitting, isValid },
  } = useFormContext();
  return (
    <CompactFormToolbar>
      <CompactSaveButton
        saving={isSubmitting}
        disabled={isSubmitting || !isValid}
      />
    </CompactFormToolbar>
  );
};

export const ChangeFleetManagerPasswordForm = ({ onFinished }: Props) => {
  const translate = useTranslate();
  const [loading, setLoading] = useState<boolean>(true);
  const [nop, setNop] = useState<boolean>(false);
  const { identity, loading: identityLoading } = useGetIdentity();
  const notify = useNotify();
  const { hub } = useSelector((x: AppState) => x.fleetManagerHub);

  const formContext = useForm<ChangeFleetManagerPasswordDto>({
    mode: "onBlur",
  });

  const { handleSubmit, control, watch } = formContext;

  useEffect(() => {
    if (!hub) {
      return;
    }

    if (nop) {
      return;
    }

    hub
      .invoke("tryRequestPasswordChangeLink")
      .then(({ isMasterAdmin, isSent }: RequestPasswordChangeLinkResultDto) => {
        if (!isMasterAdmin) {
          setLoading(false);
          return;
        }

        setNop(true);
        if (!isSent) {
          notify("messages.resetPasswordError", { type: "error" });
          onFinished();
        }

        notify("messages.resetPasswordSuccess", { type: "success" });
        onFinished();
      });
  }, [hub, notify, onFinished, nop]);

  const onSubmit = useCallback(
    (data: ChangeFleetManagerPasswordDto) => {
      if (!hub) {
        return;
      }

      return hub
        .invoke("changePassword", data)
        .then((response) => {
          if (response.success) {
            notify("resources.admin-drivers.labels.passwordChanged", {
              type: "info",
            });

            onFinished();
          } else {
            notify(`messages.${response.errorCode}`, { type: "error" });
          }
        })
        .catch((e) => {
          console.error(e);
          notify(handleAxiosError(e), { type: "error" });
        });
    },
    [hub, notify, onFinished]
  );

  const handleSubmitInternal: SubmitHandler<ChangeFleetManagerPasswordDto> = (
    data
  ) => {
    return Promise.resolve(onSubmit(data)).catch((e: Error) => {
      notify(e.message ?? "An unexpected error has occurred", "error");
    });
  };

  if (nop) {
    return null;
  }

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Loading />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <FormProvider {...formContext}>
          <form
            className={classnames("simple-form", "form-create-edit-drawer")}
            onSubmit={handleSubmit(handleSubmitInternal, (err) =>
              console.error(err)
            )}>
            <Card>
              <CardContent>
                {!identityLoading && !!identity && (
                  <input
                    type="hidden"
                    name="username"
                    autoComplete="username"
                    value={identity.email}
                  />
                )}
                <div className="ra-input">
                  <HookFormPasswordInput<ChangeFleetManagerPasswordDto>
                    control={control}
                    name="oldPassword"
                    label="Current password"
                    rules={{
                      required: translate("ra.validation.required"),
                    }}
                    variant="outlined"
                    defaultValue=""
                    options={{ autoComplete: "current-password" }}
                  />
                </div>
                <div className="ra-input">
                  <HookFormPasswordInput<ChangeFleetManagerPasswordDto>
                    control={control}
                    name="newPassword"
                    label="New password"
                    rules={{
                      required: translate("ra.validation.required"),
                    }}
                    variant="outlined"
                    defaultValue=""
                    options={{ autoComplete: "new-password" }}
                  />
                </div>
                <div className="ra-input">
                  <HookFormPasswordInput<ChangeFleetManagerPasswordDto>
                    control={control}
                    name="repeatNewPassword"
                    label="New password (repeat)"
                    rules={{
                      required: translate("ra.validation.required"),
                      validate: (value) =>
                        value === watch("newPassword") ||
                        "Passwords don't match.",
                    }}
                    variant="outlined"
                    defaultValue=""
                    options={{ autoComplete: "new-password" }}
                  />
                </div>
              </CardContent>
            </Card>
            <DefaultCompactFormToolbar />
          </form>
        </FormProvider>
      </CardContent>
    </Card>
  );
};
